<!-- Says, permanently, that this is not your data.
     Stock IonToolbar in the warning colour — no bespoke styling, so it inherits
     whatever the platform and palette decide. -->
<template>
  <IonToolbar ref="el" color="warning" class="demo-banner">
    <IonTitle size="small">
      Demo data — nothing here is saved.
      <a href="/app">Open the real app</a>
    </IonTitle>
  </IonToolbar>
</template>

<script setup>
import { ref, onMounted, onBeforeUnmount } from 'vue';
import { IonToolbar, IonTitle } from '@ionic/vue';

const el = ref(null);
let observer = null;

/**
 * Make room for this banner.
 *
 * `ion-router-outlet` is `position: absolute; inset: 0`, so it does NOT flow
 * below a sibling — it fills the whole app and paints the page's own
 * `ion-header` at y=0, exactly where this banner already is. The banner is
 * later in paint order, so it wins, and the app's toolbar is invisible for as
 * long as demo mode is on.
 *
 * That is worse than it sounds. `/demo` is the surface the store screenshots
 * are taken from and the surface QA audits, so every picture of every app this
 * factory has produced lost its toolbar — the title, the settings button, the
 * help button — and nothing errored. It is only wrong in demo mode, which is
 * the one mode nobody develops in.
 *
 * Measured rather than hardcoded: the toolbar is 56px on iOS and 62 on
 * Material, and it grows when the text wraps on a narrow screen.
 */
function measure() {
  const host = el.value?.$el ?? el.value;
  if (!host) return;
  const h = Math.ceil(host.getBoundingClientRect().height);
  document.documentElement.style.setProperty('--demo-banner-h', `${h}px`);
}

onMounted(() => {
  measure();
  if (typeof ResizeObserver === 'function') {
    observer = new ResizeObserver(measure);
    const host = el.value?.$el ?? el.value;
    if (host) observer.observe(host);
  }
});

onBeforeUnmount(() => {
  observer?.disconnect();
  document.documentElement.style.removeProperty('--demo-banner-h');
});
</script>

<!-- Not scoped: the rule has to reach ion-router-outlet, which is not this
     component's element. The variable is unset in the real app, so the
     fallback of 0 makes this inert everywhere except demo mode. -->
<style>
.demo-banner {
  position: relative;
  z-index: 2;
}

ion-app > ion-router-outlet {
  top: var(--demo-banner-h, 0);
}
</style>
